/// Переїзд даних робочого місця з `zala` на `eatlete`.
///
/// SPEC: decisions.md D117.
///
/// ЧОМУ ЦЕ ВЗАГАЛІ ПОТРІБНО. Перейменування продукту зачепило не лише слова:
/// частина «zala» в коді була не назвою, а **адресою даних** — ключі
/// `localStorage` і назва бази IndexedDB. Змінити адресу означає не
/// «перейменувати», а «перестати бачити те, що там лежить».
///
/// Найдорожче тут — черга. У базі `zala-pos` лежать операції, яких сервер ще не
/// бачив: продажі, повернення, інкасації. Каса, яка після оновлення відкриє
/// порожню базу `eatlete-pos`, покаже «черга порожня» і буде щиро в цьому
/// впевнена, а гроші лишаться в базі, до якої ніхто вже не звертається. Це не
/// втрата зручності, це втрата чеків.
///
/// Тому міграція одноразова, ідемпотентна і виконується ДО того, як застосунок
/// уперше прочитає чергу.
///
/// Старе не видаляється, поки нове не записане: якщо копіювання обірветься
/// посеред (вкладку закрили), наступний запуск почне спочатку, а не з порожнечі.

import Dexie from 'dexie'

const OLD_DB = 'zala-pos'
const NEW_DB = 'eatlete-pos'
const STORES = ['operations', 'cache'] as const

/// Пари «було → стало» для `localStorage`. Тримаються тут, а не по місцях
/// вжитку: місць пʼять, а переїзд один.
const KEYS: readonly (readonly [string, string])[] = [
  ['zala_access', 'eatlete_access'],
  ['zala_refresh', 'eatlete_refresh'],
  ['zala_theme', 'eatlete_theme'],
  ['zala.dev.role', 'eatlete.dev.role'],
  // Номер чека. Якщо його втратити, лічильник почне з початку — і в зміні
  // зʼявиться другий чек із тим самим номером.
  ['zala.pos.checkNo', 'eatlete.pos.checkNo'],
]

/// Переносить ключі сховища. Наявне нове значення виграє: воно новіше за
/// визначенням, бо могло зʼявитись лише вже після перейменування.
export const migrateStorageKeys = (storage: Storage = localStorage): number => {
  let moved = 0
  for (const [from, to] of KEYS) {
    const value = storage.getItem(from)
    if (value === null) continue
    if (storage.getItem(to) === null) {
      storage.setItem(to, value)
      moved += 1
    }
    storage.removeItem(from)
  }
  return moved
}

/// Чи існує база з такою назвою. `databases()` є не скрізь — там, де його
/// немає, чесніше нічого не робити, ніж відкривати базу «про всяк випадок»:
/// відкриття СТВОРЮЄ її, і порожня `zala-pos` після цього виглядала б як
/// «мігрувати нема чого» вже назавжди.
const exists = async (name: string): Promise<boolean> => {
  if (typeof indexedDB.databases !== 'function') return false
  const list = await indexedDB.databases()
  return list.some((entry) => entry.name === name)
}

/// Переносить чергу і кеш зі старої бази в нову.
///
/// Повертає, скільки записів перенесено. Кидати назовні нічого не можна: якщо
/// переїзд не вдався, каса мусить відкритись і працювати — але СТАРУ базу тоді
/// не видаляємо, щоб дані лишились доступні для наступної спроби.
export const migrateDatabase = async (): Promise<number> => {
  if (!(await exists(OLD_DB))) return 0

  const old = new Dexie(OLD_DB)
  let copied = 0
  try {
    // Схему беремо з самої бази, а не описуємо вдруге: другий опис — це другий
    // шанс розійтися з першим.
    await old.open()
    const target = new Dexie(NEW_DB)
    target.version(1).stores({
      operations: 'clientOpId, status, createdAt',
      cache: 'key',
    })
    await target.open()

    for (const store of STORES) {
      if (!old.tables.some((table) => table.name === store)) continue
      const rows = await old.table(store).toArray()
      if (rows.length === 0) continue
      // `bulkPut`, а не `bulkAdd`: якщо попередня спроба встигла половину,
      // повтор має дописати решту, а не впасти на першому ж дублі.
      await target.table(store).bulkPut(rows)
      copied += rows.length
    }

    old.close()
    target.close()
    await Dexie.delete(OLD_DB)
  } catch {
    old.close()
    return copied
  }

  return copied
}

/// Один вхід для запуску застосунку.
export const migrateBrand = async (): Promise<{ keys: number; rows: number }> => {
  const keys = migrateStorageKeys()
  const rows = await migrateDatabase()
  return { keys, rows }
}
